import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronDown, ChevronRight, Filter, X, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { HelpBanner } from '@/components/ui/help-banner'
import type { PaginatedResponse, AuditLogResponse, TenantResponse } from '@/api/types'

const PRODUCT_NAMES: Record<number, string> = { 1: 'PAM', 2: 'Data AI', 3: 'BetTalk' }

function actionColor(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (action.includes('delete') || action.includes('remove')) return 'destructive'
  if (action.includes('create') || action.includes('add')) return 'default'
  if (action.includes('update') || action.includes('bind')) return 'secondary'
  return 'outline'
}

export function AuditPage() {
  const { t } = useTranslation(['audit', 'common'])
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState({
    action: '',
    actor_id: '',
    tenant_id: '',
    entity_type: '',
    product_id: '',
  })
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const limit = 20

  const queryParams = new URLSearchParams()
  queryParams.set('offset', String(offset))
  queryParams.set('limit', String(limit))
  if (filters.action) queryParams.set('action', filters.action)
  if (filters.actor_id) queryParams.set('actor_id', filters.actor_id)
  if (filters.tenant_id) queryParams.set('tenant_id', filters.tenant_id)
  if (filters.entity_type) queryParams.set('entity_type', filters.entity_type)
  if (filters.product_id) queryParams.set('product_id', filters.product_id)

  const { data, isLoading } = useQuery({
    queryKey: ['audit', offset, filters],
    queryFn: () => api.get<PaginatedResponse<AuditLogResponse>>(`/audit?${queryParams.toString()}`),
  })

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
  })
  const tenantLookup = Object.fromEntries((tenantsData?.data.data ?? []).map((t) => [t.id, t.name]))

  const logs = data?.data.data ?? []
  const total = data?.data.total ?? 0

  const activeFilterCount = Object.values(filters).filter(Boolean).length

  function clearFilters() {
    setFilters({ action: '', actor_id: '', tenant_id: '', entity_type: '', product_id: '' })
    setOffset(0)
  }

  function updateFilter(key: keyof typeof filters, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setOffset(0)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{t('title')}</h2>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-1" />{t('buttons.clearWithCount', { ns: 'common', count: activeFilterCount })}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setFiltersOpen(!filtersOpen)}>
            <Filter className="h-4 w-4 mr-1" />{t('filters', { ns: 'common' })}
          </Button>
        </div>
      </div>

      <HelpBanner storageKey="audit">
        <p>{t('help.line1')}</p>
        <p>{t('help.line2')}</p>
      </HelpBanner>

      {/* Filters panel */}
      {filtersOpen && (
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">{t('action')}</Label>
                <Input
                  placeholder={t('actionPlaceholder')}
                  value={filters.action}
                  onChange={(e) => updateFilter('action', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">{t('actorId')}</Label>
                <Input
                  type="number"
                  placeholder={t('actorIdPlaceholder')}
                  value={filters.actor_id}
                  onChange={(e) => updateFilter('actor_id', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">{t('entityType')}</Label>
                <Input
                  placeholder={t('entityTypePlaceholder')}
                  value={filters.entity_type}
                  onChange={(e) => updateFilter('entity_type', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">{t('labels.tenant', { ns: 'common' })}</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm"
                  value={filters.tenant_id}
                  onChange={(e) => updateFilter('tenant_id', e.target.value)}
                >
                  <option value="">{t('filters.allTenants', { ns: 'common' })}</option>
                  {Object.entries(tenantLookup).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{t('labels.product', { ns: 'common' })}</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm"
                  value={filters.product_id}
                  onChange={(e) => updateFilter('product_id', e.target.value)}
                >
                  <option value="">{t('filters.allProducts', { ns: 'common' })}</option>
                  <option value="1">PAM</option>
                  <option value="2">Data AI</option>
                  <option value="3">BetTalk</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Log entries */}
      {isLoading ? <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p> : (
        <>
          <div className="border rounded-md divide-y">
            {logs.map((l) => {
              const isExpanded = expandedId === l.id
              return (
                <div key={l.id}>
                  <div
                    className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : l.id)}
                  >
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    }
                    <span className="text-xs text-muted-foreground w-40 shrink-0">
                      {new Date(l.created_at).toLocaleString()}
                    </span>
                    <Badge variant={actionColor(l.action)} className="shrink-0">{l.action}</Badge>
                    <span className="text-sm">
                      {l.entity_type}
                      {l.entity_id && <span className="text-muted-foreground"> #{l.entity_id}</span>}
                    </span>
                    <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                      {l.actor_email && (
                        <span>{t('by')} <strong>{l.actor_email}</strong></span>
                      )}
                      {l.impersonated_by != null && (
                        <Badge variant="destructive" className="text-xs">
                          {t('impersonatedBy')} {l.impersonated_by_email ?? `#${l.impersonated_by}`}
                        </Badge>
                      )}
                      {l.product_id != null && (
                        <Badge variant="outline" className="text-xs">{PRODUCT_NAMES[l.product_id] ?? `Product ${l.product_id}`}</Badge>
                      )}
                      {l.tenant_id != null && (
                        <span>{tenantLookup[l.tenant_id] ?? `Tenant ${l.tenant_id}`}</span>
                      )}
                      <button
                        className="p-1 hover:text-foreground transition-colors"
                        title={t('openDetailPage')}
                        onClick={(e) => { e.stopPropagation(); navigate(`/audit/${l.id}`) }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 ml-7">
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm">{t('eventDetail')}</CardTitle>
                        </CardHeader>
                        <CardContent className="pb-3">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.id')}</span>
                              <span className="font-mono">{l.id}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.timestamp')}</span>
                              <span>{new Date(l.created_at).toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.action')}</span>
                              <Badge variant={actionColor(l.action)}>{l.action}</Badge>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.actor')}</span>
                              <span>{l.actor_email ?? (l.actor_id != null ? `#${l.actor_id}` : '—')}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.impersonatedBy')}</span>
                              <span>
                                {l.impersonated_by != null
                                  ? <><span>{l.impersonated_by_email ?? `#${l.impersonated_by}`}</span> <Badge variant="destructive" className="ml-1 text-xs">{t('impBadge')}</Badge></>
                                  : '—'}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.entity')}</span>
                              <span>{l.entity_type}{l.entity_id ? ` #${l.entity_id}` : ''}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.tenant')}</span>
                              <span>{l.tenant_id != null ? (tenantLookup[l.tenant_id] ?? l.tenant_id) : '—'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground block">{t('fields.product')}</span>
                              <span>{l.product_id != null ? (PRODUCT_NAMES[l.product_id] ?? l.product_id) : '—'}</span>
                            </div>
                          </div>
                          {l.details && (
                            <div className="mt-3">
                              <span className="text-xs text-muted-foreground block mb-1">{t('fields.details')}</span>
                              <pre className="text-xs bg-muted/40 rounded-md p-3 overflow-auto max-h-48">{tryFormatJSON(l.details)}</pre>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )
            })}
            {logs.length === 0 && (
              <div className="p-6 text-center text-muted-foreground">{t('empty')}</div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t('showingEntries', { count: logs.length, total })}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function tryFormatJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}
